/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/userLists.config.json";
import axios from "axios";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_PAY_TXS } from "../graphql";
import { VApplication } from "../../../application";
import moment from "moment-timezone";
import { pretty } from "@vlinder-web/utils-react";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IUserListsMethods extends IPluginMethodMap {
  getUserListsProps(args: any): Promise<any>;
}

export class UserListsService implements IAgentPlugin {
  readonly methods: IUserListsMethods;

  constructor() {
    this.methods = {
      getUserListsProps: this.getUserListsProps.bind(this),
    };
  }

  async getUserListsProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    let filter = {};

    const searchType = this.determineSearchType(args?.criteria);
    if (searchType && searchType !== null) {
      if (searchType === "multi") {
        filter = this.getMultiFilter(args?.criteria);
      } else {
        filter = this.convertCriteria(args?.criteria);
      }
    }
    const options = {
      limit: args?.size ?? 10,
      skip: args?.offset ?? 0,
      sort: {
        field: "updatedAt",
        order: "desc",
      },
    };
    try {
      if (client) {
        const res = await client.query({
          query: GET_PAY_TXS,
          variables: {
            options: options,
            filters: filter,
          },
          fetchPolicy: "network-only",
        });
        if (res?.data) {
          console.log(
            "res?.data?.getPayTxs?.content",
            pretty(res?.data?.getPayTxs?.content)
          );
          let _rowData = this.formatUserTable(res?.data?.getPayTxs?.content);

          let _totalPages = res?.data?.getPayTxs?.count
            ? Math.ceil(parseInt(res?.data?.getPayTxs?.count) / 10)
            : 1;

          let _currentPage =
            res?.data?.getPayTxs?.skip / res?.data?.getPayTxs?.limit + 1;
          return {
            ...config,
            ultUsers: _rowData,
            isEmpty: res?.data?.getPayTxs?.content?.length <= 0,
            activePage: _currentPage,
            currentPage: _currentPage,
            pageSize: res?.data?.getPayTxs?.limit,
            totalCount: res?.data?.getPayTxs?.count,
            totalPages: _totalPages,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private formatUserTable(data: any) {
    let _rowData: any = [];
    data.forEach((item: any) => {
      _rowData.push({
        id: item?.id,
        amount: item?.amount ?? "Null",
        name: item?.masqUser?.fullName ?? "Null Null",
        email: item?.masqUser?.email ?? "Null",
        costumeID: item?.costumeID ?? "-",
        createdAt: item?.createdAt
          ? this.formatCreatedDate(item?.createdAt)
          : "Null",
        txId: item?.txId ?? "-",
        mode: item?.mode ?? "-",
        notes: item?.notes[0] ?? "-",
        payMethod: item?.payMethod ?? "_",
        createdByUser: item?.createdByUser?.fullName ?? "-",
        orderId: item?.payOrder?.orderId ?? "-",
        paymentStatus: item?.payOrder?.paymentStatus ?? "-",
        currency: item?.currency ?? "_",
        reciptId: item?.reciptId ?? "-",
        status: item?.status ?? "-",
        resolutionStatus: item?.resolutionStatus ?? "-",
        updatedAt: item?.updatedAt
          ? this.formatCreatedDate(item?.updatedAt)
          : "-",
        allDetails: item,
      });
    });
    return _rowData;
  }

  private determineSearchType(criteria: any) {
    if (Object.keys(criteria).length === 0) {
      return null;
    } else if (Object.keys(criteria)[0] === "0") {
      return "multi";
    } else {
      return "normal";
    }
  }

  private convertCriteria(criteria: any) {
    const result: any = {};
    for (const key in criteria) {
      let value = criteria[key];
      if (typeof value === "string") {
        // Remove trailing spaces from string values
        value = value.trim();
      }

      if (key === "MasqUser___fullName" || key === "MasqUser___email") {
        result[key] = { contains: value };
      } else {
        result[key] = { eq: value };
      }
    }
    return result;
  }

  private formatCreatedDate(date: Date) {
    if (date) {
      return moment(date).format("LLL");
    }
  }

  private getMultiFilter(criteria: any) {
    const transformedCriteria: any = {};
    for (const key in criteria) {
      if (criteria.hasOwnProperty(key)) {
        const { key: filterKey, filters } = criteria[key];
        const { inputData } = filters;
        const filterType = Object.keys(filters).find(
          (key) => key !== "inputData"
        );

        if (inputData.id === "mode") {
          // If the id is "Boxes___boxStatus", assign the value directly
          transformedCriteria[inputData.id] = filters[filterType];
        } else {
          transformedCriteria[inputData.id] = {
            [filterType.toLowerCase().replace("equal", "eq")]:
              filters[filterType],
          };
        }
      }
    }
    return transformedCriteria;
  }
}
